import React, { useMemo } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useDimensions } from "@ui/hooks/use-dimensions";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style";
import { ProgressCircle } from "@ui/components/common/progress-circle";
import { colors } from "@ui/style/colors";
import { getStartedManager } from "@core/service-container";
import { useObservable } from "micro-observables";
import { getStartedSteps } from "@domain/get-started/get-started";

enum DesktopTabState {
	DEFAULT = "default",
	HOVER = "hover",
}

const DESKTOP_TAB_COLORS = {
	[DesktopTabState.DEFAULT]: colors.onboardingPrimary,
	[DesktopTabState.HOVER]: colors.onboardingPrimaryDark,
} as const satisfies Record<DesktopTabState, string>;

enum MobileTabState {
	DEFAULT = "default",
	ACTIVE = "active",
}

const MOBILE_TAB_COLORS = {
	[MobileTabState.DEFAULT]: colors.grey80,
	[MobileTabState.ACTIVE]: colors.onboardingPrimary,
} as const satisfies Record<MobileTabState, string>;

interface GetStartedTabProps {
	isSelected?: boolean;
}

export const GetStartedTab: React.FC<GetStartedTabProps> = ({ isSelected = false }) => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();
	const completedCount = useObservable(getStartedManager.completedCount);
	const progress = useMemo(() => toPercentage(completedCount, getStartedSteps.length), [completedCount]);

	const progressCircle = useMemo(
		() => (
			<ProgressCircle
				size={isDesktop ? 24 : 22}
				strokeWidth={4}
				progress={progress}
				strokeProgressColor={"rgba(255, 255, 255, 0.85)"}
				strokeBackgroundColor={"rgba(255, 255, 255, 0.35)"}
			/>
		),
		[isDesktop, progress],
	);

	return isDesktop ? (
		<DesktopContainer isSelected={isSelected}>
			{progressCircle}
			<DekstopLabelContainer className="p-size-s-bold">
				{format("tabbar.getStarted", { completedCount, totalCount: 3 })}
			</DekstopLabelContainer>
		</DesktopContainer>
	) : (
		<MobileContainer>
			<MobileProgressCircleContainer isSelected={isSelected}>{progressCircle}</MobileProgressCircleContainer>
			<MobileLabelContainer isSelected={isSelected} className="p-size-xs-extra-bold">
				{format("tabbar.getStarted.mobile")}
			</MobileLabelContainer>
		</MobileContainer>
	);
};

const toPercentage = (completedCount: number, countMax: number): number =>
	completedCount === countMax ? 100 : Math.trunc((completedCount / countMax) * 100);

const DesktopContainer = styled.div<{ isSelected: boolean }>`
	${row("XS", undefined, "center")};
	width: fit-content;
	border-radius: 10px;
	padding-inline: 10px;
	padding-block: 7px;
	background-color: ${DESKTOP_TAB_COLORS.default};
	box-shadow: 0px 3px 7px 0px ${colors.grey3};

	${({ isSelected }) =>
		!isSelected &&
		`&:hover {
		background-color: ${DESKTOP_TAB_COLORS.hover};
	}`}
`;

const DekstopLabelContainer = styled.p`
	color: ${colors.white};
`;

const MobileContainer = styled.div`
	${stack(undefined, "center", "center")};
`;

const MobileProgressCircleContainer = styled.div<{ isSelected: boolean }>`
	display: grid;
	place-items: center;
	border-radius: 50%;
	width: 28px;
	height: 28px;
	margin-block: 2px 2px;
	background-color: ${({ isSelected }) => (isSelected ? MOBILE_TAB_COLORS.active : MOBILE_TAB_COLORS.default)};
`;

const MobileLabelContainer = styled.span<{ isSelected: boolean }>`
	color: ${({ isSelected }) => (isSelected ? MOBILE_TAB_COLORS.active : MOBILE_TAB_COLORS.default)};
`;
